import {
  faExclamationCircle,
  faSave,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { getNotificationItemInfo } from "../../helpers/Converters";
import { deactivateRoundScorecards } from "../../reducers/roundSlice";
import { isSameNineTwice } from "./roundHooks";

//each round course carries the teams assigned to the holes it plays, so a
//round's teams are what its courses hold between them
export const getRoundAssignedTeamCount = (round) => {
  return (
    round?.roundCourses?.reduce?.(
      (total, roundCourse) => total + (roundCourse?.assignedTeamsCount ?? 0),
      0
    ) ?? 0
  );
};

//the assignment a team holds, in this round or in one it is linked to. a team
//can only play a round number once, so any of them takes it out of circulation.
export const getTeamHoleAssignment = (team, holeAssignments) => {
  return holeAssignments?.find?.((ha) =>
    ha?.roundHoleAssignmentTournamentTeams
      ?.map?.((rhatt) => rhatt?.tournamentTeamKey)
      ?.includes?.(team?.id)
  );
};

//the teams that hold none of the given assignments. which teams count is left to
//the caller, so a list can decide for itself whether disqualified teams belong.
export const filterUnassignedTeams = (teams, holeAssignments) => {
  return (
    teams?.filter?.(
      (team) => getTeamHoleAssignment(team, holeAssignments) === undefined
    ) ?? []
  );
};

//a team plays in the flight its main flight position points at. a team placed in
//a flight ahead of the event has no position yet, so it's found among the teams
//the flight was assigned instead. a team on a hole comes as its assignment, which
//carries the team's id as its key.
export const getTeamFlight = (team, flights) => {
  const teamID = team?.tournamentTeamKey ?? team?.id;

  return (
    flights?.find?.(
      (flight) =>
        team?.mainFlightPosition !== undefined &&
        team?.mainFlightPosition !== null &&
        flight.position === team?.mainFlightPosition
    ) ??
    flights?.find?.((flight) =>
      flight?.teamFlights?.some?.((tf) => tf?.tournamentTeam?.id === teamID)
    )
  );
};

//tee times read in the order they go off, then by course, then by hole
export const sortByTeeTimeAndCourse = (a, b) => {
  const ha1 = a[0];
  const ha2 = b[0];

  // sort by teeTime
  const teeTimeComparison = new Date(ha1.teeTime) - new Date(ha2.teeTime);
  if (teeTimeComparison !== 0) {
    return teeTimeComparison;
  }

  // if teeTime is the same, sort by course (or should we sort by scorecard position here?)
  const courseComparison =
    ha1.roundCourse?.course?.id - ha2.roundCourse?.course?.id;
  if (courseComparison !== 0) {
    return courseComparison;
  }

  // if both teeTime and course are the same, sort by hole #
  return ha1.hole - ha2.hole;
};

//a shotgun start's positions come in hole by hole, so the ones on the same hole
//are gathered together, course by course
export const groupShotgunHoleAssignments = (holeAssignments) => {
  let groups = {};
  let positions = [];

  const addGroup = (courseID, group) => {
    if (groups[courseID] === undefined) {
      groups[courseID] = [];
    }
    groups[courseID].push([...group]);
  };

  holeAssignments?.forEach?.((ha) => {
    // group by course, then hole number
    if (positions.length === 0 || positions[0].hole === ha.hole) {
      positions.push(ha);
    } else {
      addGroup(ha.roundCourse?.course?.id, positions);
      positions = [ha];
    }
  });
  if (positions.length > 0) {
    addGroup(positions[0].roundCourse?.course?.id, positions);
  }

  return Object.values(groups).flat();
};

//a tee time start's positions are gathered by the hole and time they go off
//together, in tee time order
export const groupTeeTimeHoleAssignments = (holeAssignments) => {
  let groups = [];
  let positions = [];

  holeAssignments?.forEach?.((ha) => {
    // group assignments by teeTime and hole
    if (
      positions.length === 0 ||
      (positions[0].hole === ha.hole && positions[0].teeTime === ha.teeTime)
    ) {
      positions.push(ha);
    } else {
      groups.push([...positions]);
      positions = [ha];
    }
  });
  if (positions.length > 0) {
    groups.push([...positions]);
  }

  return groups.sort(sortByTeeTimeAndCourse);
};

//the banner the hole assignments page sits under when nothing else is being
//reported. assignments save as they happen, so that is all it says, unless the
//round plays one nine twice (the second nine starts blocked, which the user can
//dismiss once read) or scorecards have already been made for it.
export const useHoleAssignmentBanner = (round, roundID) => {
  const dispatch = useDispatch();
  const [showWarningBanner, setShowWarningBanner] = useState(true);

  return useMemo(() => {
    let banner = {
      title: getNotificationItemInfo(
        `Assignments will automatically be saved.`,
        faSave,
      ),
      state: "grey",
    };

    if (!round?.hasScorecards || round?.state === "live") {
      if (
        isSameNineTwice(round?.roundCourses) &&
        round?.startType !== "interval_start" &&
        showWarningBanner
      ) {
        banner = {
          title: getNotificationItemInfo(
            "All assignment slots are available for the first nine, while the second nine is blocked--but you can unblock them.",
            faExclamationCircle,
            "This round will play 18 holes on a single 9-hole course",
          ),
          state: "warning",
          bannerActions: [
            {
              rightIcon: faTimesCircle,
              actionClick: () => {
                setShowWarningBanner(false);
              },
            },
          ],
        };
      }
    } else {
      banner = {
        title: getNotificationItemInfo(
          `Adding new scorecards might have undesired effects.`,
          faExclamationCircle,
          "Scorecards have already been generated",
        ),
        state: "warning",
        bannerActions: [
          {
            title: "clear scorecards",
            actionClick: () => {
              dispatch(deactivateRoundScorecards(roundID));
            },
          },
        ],
      };
    }

    return banner;
  }, [
    dispatch,
    round?.hasScorecards,
    round?.state,
    round?.startType,
    round?.roundCourses,
    roundID,
    showWarningBanner,
  ]);
};
